import { supabase } from "@/integrations/supabase/client";
import { templateFieldsQuery } from "@/integrations/supabase/templateEditor";
import { toast } from "sonner";
import { useTemplateWithFields } from "./useTemplateWithFields";
import { log } from "@/utils/debug";

export const useTemplateFields = ({ templateId }: { templateId?: string } = {}) => {
  const { template, setQueryData, invalidateQuery } = useTemplateWithFields();
  if (!template) {
    throw new Error("Template not found - this hook should not be used outside of a TemplateWithFieldsProvider");
  }

  const templateFields = template.template_fields;

  const handleAddField = async (fieldId: string) => {
    try {
      // Get the current highest order_index
      const currentFields = templateFields ?? [];
      const highestOrderIndex = currentFields.reduce((max, field) => Math.max(max, field.order_index), -1);

      // Create the new template field
      const { data: newField, error } = await supabase
        .from("template_fields")
        .insert({
          template_id: template.id,
          field_id: fieldId,
          order_index: highestOrderIndex + 1,
          is_visible: true,
        })
        .select(templateFieldsQuery)
        .single();

      if (error) throw error;

      // Update the query cache with the new field
      setQueryData((oldData) => {
        if (!oldData.template_fields)
          return {
            ...oldData,
            template_fields: [newField],
          };
        return {
          ...oldData,
          template_fields: [...oldData.template_fields, newField],
        };
      });

      // Trigger a refetch to ensure we have all the related data
      invalidateQuery().catch((error) => {
        console.error("Error invalidating query after adding templatefield:", error);
      });

      toast.success("Field added successfully");
      return newField;
    } catch (error) {
      console.error("Error adding field:", error);
      toast.error("Failed to add field");
      throw error;
    }
  };

  const handleRemoveField = async (templateFieldId: string) => {
    try {
      const { error } = await supabase.from("template_fields").delete().eq("id", templateFieldId);

      if (error) throw error;

      // Update the query cache by removing the deleted field
      setQueryData((oldData) => {
        if (!oldData.template_fields) return oldData;
        return {
          ...oldData,
          template_fields: oldData.template_fields.filter((field) => field.id !== templateFieldId),
        };
      });

      // Trigger a refetch to ensure all data is up to date
      invalidateQuery().catch((error) => {
        console.error("Error invalidating query after removing template field", error);
      });

      toast.success("Field removed successfully");
    } catch (error) {
      console.error("Error removing field:", error);
      toast.error("Failed to remove field");
      throw error;
    }
  };

  const handleMoveField = async (templateFieldId: string, newOrderIndex: number) => {
    try {
      // Get current template fields
      const currentFields = templateFields ?? [];
      const fieldToMove = currentFields.find((f) => f.id === templateFieldId);

      if (!fieldToMove) {
        throw new Error("Field not found");
      }

      const oldOrderIndex = fieldToMove.order_index;

      log.process(`[handleMoveField] Moving field ${templateFieldId} from index ${oldOrderIndex} to ${newOrderIndex}`);

      // If the order hasn't changed, do nothing
      if (oldOrderIndex === newOrderIndex) {
        return;
      }

      // Calculate new order indices for all fields
      const fieldsToUpdate = currentFields.map((templateField) => {
        // Create a copy of each field with all necessary properties
        const fieldCopy = {
          id: templateField.id,
          field_id: templateField.field_id,
          template_id: templateField.template_id,
          order_index: templateField.order_index,
          is_visible: templateField.is_visible,
        };

        // Field being moved
        if (templateField.id === templateFieldId) {
          fieldCopy.order_index = newOrderIndex;
          return fieldCopy;
        }

        // Adjust other fields based on movement direction
        if (oldOrderIndex < newOrderIndex) {
          // Moving down - shift fields in between up
          if (templateField.order_index > oldOrderIndex && templateField.order_index <= newOrderIndex) {
            fieldCopy.order_index = templateField.order_index - 1;
          }
        } else {
          // Moving up - shift fields in between down
          if (templateField.order_index >= newOrderIndex && templateField.order_index < oldOrderIndex) {
            fieldCopy.order_index = templateField.order_index + 1;
          }
        }

        return fieldCopy;
      });

      // Update all fields in a single transaction
      const { error: batchError } = await supabase.from("template_fields").upsert(fieldsToUpdate, {
        onConflict: "id",
      });

      if (batchError) throw batchError;

      // Now update the query cache
      setQueryData((oldData) => {
        if (!oldData.template_fields) return oldData;

        // Sort the fields according to their new order_index
        const updatedFields = [...oldData.template_fields];

        // For each field, update its order index without losing any other properties
        updatedFields.forEach((field) => {
          const updatedField = fieldsToUpdate.find((f) => f.id === field.id);
          if (updatedField) {
            field.order_index = updatedField.order_index;
          }
        });

        // Sort the fields by order_index to ensure correct display order
        updatedFields.sort((a, b) => a.order_index - b.order_index);

        log.process(
          `[handleMoveField] Updated cache with new field order:`,
          updatedFields.map((f) => ({ id: f.id, field_name: f.field?.name, order: f.order_index })),
        );

        return {
          ...oldData,
          template_fields: updatedFields,
        };
      });

      // Always invalidate the query to ensure full update with all nested data
      await invalidateQuery();

      toast.success("Field order updated successfully");
    } catch (error) {
      console.error("Error updating field order:", error);
      toast.error("Failed to update field order");
      throw error;
    }
  };

  return {
    templateFields,
    handleAddField,
    handleRemoveField,
    handleMoveField,
  };
};
